// Ruty app - versjon 2.1
import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { TransportModeProvider } from "@/contexts/TransportModeContext";
import { FerryDepartureProvider } from "@/contexts/FerryDepartureContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";

import { BottomNav } from "@/components/BottomNav";
import { OfflineBanner } from "@/components/OfflineBanner";
import { useAlertTrigger } from "@/hooks/useAlertTrigger";
import { useFavoritesInventorySnapshot } from "@/hooks/useFavoritesInventorySnapshot";

// Lazy load non-critical UI components
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import("@/components/CookieBanner").then(m => ({ default: m.CookieBanner })));
const PWAInstallPrompt = lazy(() => import("@/components/PWAInstallPrompt").then(m => ({ default: m.PWAInstallPrompt })));

// Lazy load non-critical pages for better initial load performance
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Help = lazy(() => import("./pages/Help"));
const Guide = lazy(() => import("./pages/Guide"));
const FerryDock = lazy(() => import("./pages/FerryDock"));
const FerryRoutePage = lazy(() => import("./pages/FerryRoute"));
const FerryRouteList = lazy(() => import("./pages/FerryRouteList"));
const FerryDockList = lazy(() => import("./pages/FerryDockList"));
const FerryTypeList = lazy(() => import("./pages/FerryTypeList"));
const FerryTypeDetail = lazy(() => import("./pages/FerryTypeDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Status = lazy(() => import("./pages/Status"));
const FerryWidget = lazy(() => import("./pages/FerryWidget"));
const Ferry = lazy(() => import("./pages/Ferry"));
const Search = lazy(() => import("./pages/Search"));
const Alerts = lazy(() => import("./pages/Alerts"));
const MineFerger = lazy(() => import("./pages/MineFerger"));
const AdminPushLog = lazy(() => import("./pages/AdminPushLog"));
const Oppgrader = lazy(() => import("./pages/Oppgrader"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 30 * 60 * 1000,
      // Retry is OFF by default – edgeFn handles its own retry (1x on 5xx/network).
      // This prevents double-retry (max 2 calls total via edgeFn).
      // Individual queries can override with retry: 1 if they don't use edgeFn.
      retry: false,
      // Don't refetch on window focus – avoids unexpected re-fetches on tab switch
      refetchOnWindowFocus: false,
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

/**
 * Strips the ?lang= query parameter from the URL via a client-side replace,
 * so search engines never index duplicate URLs with language params.
 * Language preference itself is handled separately by LanguageContext.
 */
const LangParamStripper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    if (params.has("lang")) {
      params.delete("lang");
      const cleanSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: cleanSearch ? `?${cleanSearch}` : "", hash: location.hash },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, location.hash, navigate]);
  return null;
};

function AlertTriggerMount() {
  useAlertTrigger();
  return null;
}

function FavoritesInventoryMount() {
  useFavoritesInventorySnapshot();
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
          <BrowserRouter>
            <LangParamStripper />
            <AuthProvider>
              <FavoritesProvider>
              <HelmetProvider>
              <FerryDepartureProvider>
              <TransportModeProvider>
                <OfflineBanner />
                <AlertTriggerMount />
                <FavoritesInventoryMount />
                <Suspense fallback={null}>
                  <CookieBanner />
                  <PWAInstallPrompt />
                </Suspense>
                <ErrorBoundary pageName="appen">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Legacy redirects must be explicit and come before the catch-all route. */}
                      <Route path="/logg-inn" element={<Navigate to="/auth" replace />} />
                      <Route path="/veiledning" element={<Navigate to="/hjelp" replace />} />
                      {/* Common spelling mistake (ferje vs. ferge) – redirect instead of 404. */}
                      <Route path="/ferje" element={<Navigate to="/ferge" replace />} />

                      <Route path="/" element={
                        <ErrorBoundary pageName="forsiden">
                          <Ferry />
                        </ErrorBoundary>
                      } />
                      <Route path="/kollektiv" element={
                        <ErrorBoundary pageName="kollektivsiden">
                          <Index />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge" element={
                        <ErrorBoundary pageName="fergesiden">
                          <Ferry />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge/planlegg" element={
                        <ErrorBoundary pageName="planlegg-reise">
                          <Ferry />
                        </ErrorBoundary>
                      } />
                      {/* Alias → canonical /ferge (client replace; hosting 301 is separate). */}
                      <Route path="/ferger" element={<Navigate to="/ferge" replace />} />
                      <Route path="/ferge/kai" element={
                        <ErrorBoundary pageName="fergekai-liste">
                          <FerryDockList />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge/kai/:slug" element={
                        <ErrorBoundary pageName="fergekai">
                          <FerryDock />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge/samband" element={
                        <ErrorBoundary pageName="fergesamband-liste">
                          <FerryRouteList />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge/samband/:slug" element={
                        <ErrorBoundary pageName="fergesamband">
                          <FerryRoutePage />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge/fergetyper" element={
                        <ErrorBoundary pageName="fergetyper">
                          <FerryTypeList />
                        </ErrorBoundary>
                      } />
                      <Route path="/ferge/fergetyper/:slug" element={
                        <ErrorBoundary pageName="fergetype">
                          <FerryTypeDetail />
                        </ErrorBoundary>
                      } />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/personvern" element={<Privacy />} />
                      <Route path="/cookies" element={<CookiePolicy />} />
                      <Route path="/vilkar" element={<Terms />} />
                      <Route path="/om-oss" element={<About />} />
                      <Route path="/kontakt" element={<Contact />} />
                      <Route path="/hjelp" element={<Help />} />
                      <Route path="/guide" element={<Guide />} />
                      <Route path="/ferge/widget/:slug" element={<FerryWidget />} />
                      <Route path="/status" element={<Status />} />
                      <Route path="/sok" element={
                        <ErrorBoundary pageName="søkesiden">
                          <Search />
                        </ErrorBoundary>
                      } />
                      <Route path="/varsler" element={
                        <ErrorBoundary pageName="varselsenter">
                          <Alerts />
                        </ErrorBoundary>
                      } />
                      <Route path="/mine-ferger" element={
                        <ErrorBoundary pageName="mine-ferger">
                          <MineFerger />
                        </ErrorBoundary>
                      } />
                      <Route path="/admin/push-log" element={
                        <ErrorBoundary pageName="admin-push-log">
                          <AdminPushLog />
                        </ErrorBoundary>
                      } />
                      <Route path="/oppgrader" element={
                        <ErrorBoundary pageName="oppgrader">
                          <Oppgrader />
                        </ErrorBoundary>
                      } />
                      <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
                <BottomNav />
              </TransportModeProvider>
              </FerryDepartureProvider>
              </HelmetProvider>
              </FavoritesProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
